import { useMutation, useQueryClient } from 'react-query';

import type { IStandardErrorResponse } from 'src/api/common';
import type { ISnippet, ICreateSnippetRequest } from 'src/services/api/snippets';
import { useNotifications } from 'src/notifications';
import { queryKeys } from 'src/services/api/constants';
import { apiClient } from 'src/services/api/client';
import { getApiErrorMessage } from 'src/services/api/utils';
import { GET_SNIPPETS_PATH } from 'src/services/api/snippets/constants';

type ICreateSnippetResponse = {
    snippet: ISnippet;
}

export const useCreateSnippet = () => {
    const queryClient = useQueryClient();
    const { addNotification } = useNotifications();

    return useMutation<
    ISnippet,
    IStandardErrorResponse,
    ICreateSnippetRequest
    >(
        async (payload) => {
            const res = await apiClient(GET_SNIPPETS_PATH, {
                method: 'post',
                json: payload,
            }).json<ICreateSnippetResponse>();

            return res.snippet;
        }, {
            onSuccess: (snippet) => {
                addNotification('Snippet created successfully', 'success');

                queryClient.setQueryData<ISnippet[]>(queryKeys.snippets, (oldData) => {
                    if (!oldData) {
                        return [snippet];
                    }

                    return [...oldData, snippet];
                });

                queryClient.setQueryData<ISnippet>([queryKeys.snippets, snippet.id], snippet);
                queryClient.invalidateQueries(queryKeys.achievements);
            },
            onError: (error) => {
                const errorMessage = getApiErrorMessage(error) || 'Failed to create snippet';
                addNotification(errorMessage, 'error');
            }
        });
};